import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { comparisonFeatures, freezones as mockFreezones } from '../mock';
import { getFreezones } from '../api';
import { Button } from '../components/ui/button';
import { Check, X, Info, ArrowRight, Building2, Globe } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const Comparison = () => {
  const { t, pick, lang, dir } = useLang();
  const [tab, setTab] = useState('side');
  const [params] = useSearchParams();
  const highlight = params.get('type');
  const [freezones, setFreezones] = useState(mockFreezones);

  useEffect(() => {
    getFreezones().then((d) => d && d.length && setFreezones(d.map(z => ({ ...z, minCost: z.min_cost, id: z.code })))).catch(() => {});
  }, []);

  const renderCell = (val) => {
    if (val === true) return <Check className="w-5 h-5 text-blue-400 mx-auto" />;
    if (val === false) return <X className="w-5 h-5 text-rose-400 mx-auto" />;
    return <span className="text-[#111827]/85">{pick(val)}</span>;
  };

  return (
    <div className="pt-28 pb-16 min-h-screen" dir={dir}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-4">— {t('nav.compare')} —</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#0a0a0a]">
            {t('compare.title_1')} <span className="text-[#2563eb]/70 italic">{t('compare.vs')}</span> <span className="gold-text">{t('compare.title_2')}</span>
          </h1>
          <p className="mt-4 text-[#111827]/60 max-w-2xl mx-auto">{t('compare.subtitle')}</p>

          <div className="inline-flex mt-8 p-1 rounded-full bg-[#ffffff] border border-[#2563eb]/20">
            <button onClick={() => setTab('side')} className={`px-5 py-2 rounded-full text-sm transition-all ${tab === 'side' ? 'gold-button' : 'text-[#111827]/70'}`}>{t('compare.side_by_side')}</button>
            <button onClick={() => setTab('zones')} className={`px-5 py-2 rounded-full text-sm transition-all ${tab === 'zones' ? 'gold-button' : 'text-[#111827]/70'}`}>{t('compare.explore_zones')}</button>
          </div>
        </div>

        {tab === 'side' && (
          <TooltipProvider delayDuration={100}>
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="grid grid-cols-3 border-b border-[#2563eb]/15">
                <div className="p-5 text-xs uppercase tracking-[0.2em] text-[#111827]/50">{t('compare.feature')}</div>
                <div className={`p-5 text-center border-x border-[#2563eb]/10 ${highlight === 'mainland' ? 'bg-[#2563eb]/8' : ''}`}>
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#2563eb]/10 border border-[#2563eb]/30">
                    <Building2 className="w-3.5 h-3.5 text-[#3b82f6]" />
                    <span className="text-sm text-[#3b82f6] font-medium">{t('compare.mainland')}</span>
                  </div>
                </div>
                <div className={`p-5 text-center ${highlight === 'freezone' ? 'bg-blue-500/8' : ''}`}>
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/30">
                    <Globe className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-sm text-blue-400 font-medium">{t('compare.freezone')}</span>
                  </div>
                </div>
              </div>
              {comparisonFeatures.map((f, i) => (
                <div key={f.key} className={`grid grid-cols-3 border-b border-[#2563eb]/10 last:border-0 hover:bg-[#2563eb]/3 transition-colors`}>
                  <div className="p-5 text-sm text-[#111827]/85 flex items-center gap-2">
                    <span>{pick(f.label)}</span>
                    <Tooltip>
                      <TooltipTrigger><Info className="w-3.5 h-3.5 text-[#111827]/30 hover:text-[#2563eb]" /></TooltipTrigger>
                      <TooltipContent className="bg-[#ffffff] border-[#2563eb]/30 text-[#111827] max-w-xs text-xs">{pick(f.tooltip)}</TooltipContent>
                    </Tooltip>
                  </div>
                  <div className={`p-5 text-center text-sm border-x border-[#2563eb]/5 ${highlight === 'mainland' ? 'bg-[#2563eb]/5' : ''}`}>{renderCell(f.mainland)}</div>
                  <div className={`p-5 text-center text-sm ${highlight === 'freezone' ? 'bg-blue-500/5' : ''}`}>{renderCell(f.freezone)}</div>
                </div>
              ))}
            </div>
          </TooltipProvider>
        )}

        {tab === 'zones' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {freezones.map((z) => (
              <div key={z.id} className="glass-card rounded-2xl p-7 group hover:-translate-y-1 transition-all relative">
                {z.popular && (
                  <div className="absolute top-4 end-4 px-2.5 py-0.5 rounded-full bg-[#2563eb]/15 border border-[#2563eb]/30 text-[10px] uppercase tracking-widest text-[#3b82f6]">Popular</div>
                )}
                <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] mb-2">{pick(z.category)}</div>
                <h3 className="font-display text-2xl text-[#0a0a0a] mb-2">{pick(z.name)}</h3>
                <p className="text-xs text-[#111827]/60 leading-relaxed mb-6 min-h-[40px]">{pick(z.description)}</p>
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[#2563eb]/10">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#111827]/40">{lang === 'ar' ? 'تبدأ من' : 'From'}</div>
                    <div className="font-display text-lg text-[#3b82f6] mt-0.5">AED {z.minCost.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#111827]/40">{lang === 'ar' ? 'تأشيرات' : 'Visas'}</div>
                    <div className="font-display text-lg text-blue-400 mt-0.5">{z.visas}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="grid md:grid-cols-2 gap-5 mt-12">
          <Link to="/SetupWizard?type=mainland" className="glass-card rounded-2xl p-8 group hover:-translate-y-1 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl text-[#0a0a0a]">{t('compare.start_ml')}</h3>
              <ArrowRight className="w-5 h-5 text-[#2563eb] rtl:hidden group-hover:translate-x-1 transition-transform" />
            </div>
            <p className="text-sm text-[#111827]/60">{t('compare.begin_ml')}</p>
            <Button className="gold-button rounded-md h-11 px-6 mt-6 text-sm">{t('cta.get_started')}</Button>
          </Link>
          <Link to="/SetupWizard?type=freezone" className="glass-card rounded-2xl p-8 group hover:-translate-y-1 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl text-[#0a0a0a]">{t('compare.start_fz')}</h3>
              <ArrowRight className="w-5 h-5 text-blue-400 rtl:hidden group-hover:translate-x-1 transition-transform" />
            </div>
            <p className="text-sm text-[#111827]/60">{t('compare.begin_fz')}</p>
            <Button className="rounded-md h-11 px-6 mt-6 text-sm bg-blue-500/90 hover:bg-blue-500 text-white">{t('cta.get_started')}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Comparison;
